import { NodeInterface__factory } from "@arbitrum/sdk/dist/lib/abi/factories/NodeInterface__factory";
import { NODE_INTERFACE_ADDRESS } from "@arbitrum/sdk/dist/lib/dataEntities/constants";
import { ercAbi } from "@umi/shared/abis/erc";
import { disperseAbi } from "@umi/shared/abis/umi/disperse";
import { lensAbi } from "@umi/shared/abis/umi/lens";
import { normalizeAddress } from "@umi/shared/address";
import { bufferBigint } from "@umi/shared/bigint";
import { catchBlock } from "@umi/shared/catchBlock";
import { Chain, ChainTag, EvmChainTag, getChain, OP_CHAIN_TAGS, ZK_CHAIN_TAGS, type RpcProvider } from "@umi/shared/chains";
import { getCrossPlatformInfo } from "@umi/shared/crossplatform";
import {
  ContractNotConfiguredError,
  UnknownTokenStandardError
} from "@umi/shared/errors";
import { retry } from "@umi/shared/retry";
import type { JsonRpcResponse } from "@umi/types/rpc";
import { Token } from "@umi/types/token";
import { HydratedWallet } from "@umi/types/wallet";
import Dataloader from "dataloader";
import { BytesLike } from "ethers";
import _ from "lodash";
import { ofetch } from "ofetch";
import Queue from "p-queue";
import {
  Address,
  BlockNumber,
  BlockTag,
  Client,
  createPublicClient,
  createWalletClient,
  encodeFunctionData,
  erc20Abi, erc721Abi,
  formatTransactionReceipt,
  getContract,
  Hash,
  Hex,
  hexToNumber,
  http,
  RpcRequestError,
  SignedAuthorization,
  StateOverride,
  TransactionReceipt,
  Transport,
  WaitForTransactionReceiptTimeoutError,
  WalletClient,
  zeroAddress,
  type GetContractReturnType,
  type PublicClient
} from "viem";
import {
  generatePrivateKey,
  privateKeyToAccount,
  type PrivateKeyToAccountOptions
} from "viem/accounts";
import {
  publicActionsL2 as opPublicActionsL2,
  type PublicActionsL2 as OpPublicActionsL2
} from "viem/op-stack";
import {
  publicActionsL2 as zkSyncPublicActionsL2,
  type PublicActionsL2 as ZkSyncPublicActionsL2
} from "viem/zksync";
import { queuedHttp } from "./transports/queued";
import {
  createDebugClient,
  probeDebugTraceSupport,
  type DebugClient,
} from "./utils/debugTrace";
import { viemTransportToProvider } from "./utils/ethers";

export { createDebugClient, debugActions, probeDebugTraceSupport } from "./utils/debugTrace";
export type {
  CallTracerCallType,
  CallTracerFrame,
  CallTracerLog,
  DebugTraceTransactionCallTracerRequest,
  DebugTraceTransactionCallTracerResult
} from "./utils/debugTrace";


type ExtendedClient = PublicClient & OpPublicActionsL2 & ZkSyncPublicActionsL2;
export type GasFeeEstimation = { fee: bigint, gas: bigint, feePerGas: bigint };

export type AccountType = "eoa" | "contract" | "eip7702";

// Maps the uint8 codes returned by Lens.classifyAccounts
const ACCOUNT_TYPE_BY_CODE: Record<number, AccountType> = { 0: "eoa", 1: "contract", 2: "eip7702" };

export const extendClient = <T extends Client>(client: T, chainTag: EvmChainTag) => {
  if (ZK_CHAIN_TAGS.includes(chainTag)) return client.extend(zkSyncPublicActionsL2()) as unknown as ExtendedClient;
  if (OP_CHAIN_TAGS.includes(chainTag)) return client.extend(opPublicActionsL2()) as unknown as ExtendedClient;
  return client as unknown as ExtendedClient;
};

// Entries per nonce JSON-RPC POST. Matches this client's http `batch.batchSize` and stays under the 100-item cap common on public RPCs.
const NONCE_RPC_BATCH_SIZE = 100;

export class EvmManager {

  // Clients
  public publicClient: ExtendedClient; // Using public RPC
  public debugClient: DebugClient;
  public httpTransport: Transport;
  public httpRpcUrl: string;
  private useJwtAuth: boolean;

  get transportHeaders(): Record<string, string> {
    if (!this.useJwtAuth) return {};
    const [jwt = ""] = catchBlock(() => globalThis.localStorage?.getItem("umi.jwt") || "");
    if (!jwt) return {};
    return { Authorization: `Bearer ${jwt}` };
  }

  public chainTag: EvmChainTag;
  public cache: Map<string, any> = new Map();
  public chain: Chain<"EVM">;
  public viem: Chain<"EVM">["viem"];

  private receiptLoader!: Dataloader<Hash, TransactionReceipt | null>;
  private nonceLoader!: Dataloader<{ address: Address; blockTag: BlockTag }, number>;
  private debugTraceSupported?: boolean;
  private debugTraceProbe?: Promise<boolean>;

  constructor({
    chainTag,
    provider,
    rpcUrl,
    transportType,
  }: {
    chainTag: EvmChainTag,
    provider?: RpcProvider,
    rpcUrl?: string | null,
    transportType?: "default" | "queue",
  }) {

    const chain   = getChain(chainTag);
    this.chain    = chain;
    this.viem     = this.chain.viem;
    this.chainTag = chainTag;
    provider      = provider ?? chain.defaultProvider;

    /**
     * The umi provider is a browser RPC proxy that authenticates with
     * the user JWT. The backend talks to provider RPC URLs directly.
     */
    const { platform } = getCrossPlatformInfo();

    // If we are in the browser and using the umi RPC proxy, attach JWT auth.
    this.useJwtAuth = platform === "browser" && provider === "umi";

    this.httpRpcUrl = rpcUrl ?? chain.provider(provider).url;

    // Queued transport is for public RPCs (rate-limited).
    const resolvedTransportType = transportType ?? (provider === "public" ? "queue" : "default");

    this.httpTransport = (() => {
      if (resolvedTransportType === "default") {
        return http(this.httpRpcUrl, {
          timeout: 10_000,
          retryCount: 3,
          batch: {
            batchSize: 100,
            wait: 10
          },
          onFetchRequest: (_request, init) => ({
            ...init,
            headers: {
              ...init.headers,
              ...this.transportHeaders,
            },
          }),
          onFetchResponse: (response) => {
            if (this.useJwtAuth && response.status === 401 && platform === "browser") {
              // @ts-expect-error window is defined in the browser
              window.dispatchEvent(new CustomEvent("umi:session-expired"));
            }
          }
        });
      }
      return queuedHttp(chainTag, this.httpRpcUrl);
    })();


    /**
     * HTTP transport. Browser traffic to the umi proxy sends the user JWT.
     * @note Public RPCs use @see queuedHttp to respect rate limits.
     */
    this.publicClient = extendClient(createPublicClient({
      chain: this.viem,
      transport: this.httpTransport,
      batch: {
        multicall: {
          batchSize: 1024,
          wait: 10,
        }
      }
    }), chainTag);

    this.debugClient = createDebugClient({
      chain: this.viem,
      transport: this.httpTransport,
    });

    this.receiptLoader = new Dataloader<Hash, TransactionReceipt | null>(
      async (hashes) => {
        const payload = hashes.map((hash, i) => ({
          jsonrpc: "2.0",
          id: i,
          method: "eth_getTransactionReceipt",
          params: [hash],
        }));

        const [json, err] = await catchBlock(() => {
          return ofetch<JsonRpcResponse[]>(this.httpRpcUrl, {
            method: "POST",
            body: payload,
            headers: this.transportHeaders,
          });
        });


        if (err) return hashes.map(() => err);

        return json.map(({ result, error }) => {
          if (error) return new Error(error.message);
          if (!result) return null;
          const [receipt, parseErr] = catchBlock(() => formatTransactionReceipt(result as any));
          if (parseErr) return parseErr;
          return receipt;
        });
      },
      {
        cache: false,
        batchScheduleFn: (cb) => setTimeout(cb, 100),
      }
    );

    this.nonceLoader = new Dataloader<{ address: Address; blockTag: BlockTag }, number>(
      async (keys) => {
        const payload = keys.map((key, i) => ({
          jsonrpc: "2.0",
          id: i,
          method: "eth_getTransactionCount",
          params: [key.address, key.blockTag],
        }));

        const [json, err] = await catchBlock(() => {
          return ofetch<JsonRpcResponse[]>(this.httpRpcUrl, {
            method: "POST",
            body: payload,
            headers: this.transportHeaders,
          });
        });

        if (err) return keys.map(() => err);

        // JSON-RPC batch responses are not required to preserve request order.
        const results: Array<number | Error> = keys.map((_, i) => new Error(`missing JSON-RPC response for id ${i}`));
        for (const { id, result, error } of json) {
          if (!Number.isInteger(id) || id < 0 || id >= keys.length) continue;
          results[id] = error
            ? new RpcRequestError({ body: payload, error, url: this.httpRpcUrl })
            : hexToNumber(result as Hex);
        }
        return results;
      },
      {
        cache: false,
        maxBatchSize: NONCE_RPC_BATCH_SIZE,
        batchScheduleFn: (cb) => setTimeout(cb, 10),
      }
    );

  }


  static isValidAddress(address: string) {
    if (!address) return false;
    return /^0x[0-9a-fA-F]{40}$/.test(address);
  }


  static createPk() { return generatePrivateKey(); }


  static toAddress(pk: string) {
    const account = EvmManager.safePrivateKeyToAccount(pk as Hex);
    return normalizeAddress(account.address);
  }


  /**
   * Return the token metadata for a given token address.
   * @param address
   * @returns
   */
  async getToken(address: Address) {

    const [
      { result: symbol },
      { result: name = "" },
      { result: isErc721 },
      { result: isErc1155 },
      { result: decimals }
    ] = await this.publicClient.multicall({
      allowFailure: true,
      contracts: [
        {
          address,
          abi: erc721Abi,
          functionName: "symbol",
        },
        {
          address,
          abi: erc721Abi,
          functionName: "name",
        },
        {
          address,
          abi: ercAbi.erc1155, // ERC721 also implements this interface
          functionName: "supportsInterface",
          args: ["0x80ac58cd"], // ERC721 interface ID
        },
        {
          address,
          abi: ercAbi.erc1155,
          functionName: "supportsInterface",
          args: ["0xd9b67a26"], // ERC1155 interface ID
        },
        {
          address,
          abi: erc20Abi,
          functionName: "decimals",
        }
      ],
    });

    if (!isErc721 && !isErc1155 && !_.isNumber(decimals)) return null;

    const standard = (() => {
      if (isErc721) return "erc721";
      else if (isErc1155) return "erc1155";
      else if (_.isNumber(decimals)) return "erc20";
      return null;
    })();

    if (!standard) throw new UnknownTokenStandardError(address);

    if (standard === "erc20") {
      return {
        type: "coin",
        address: address,
        standard,
        symbol: symbol || name.toUpperCase(),
        name,
        decimals: decimals as number,
      } satisfies Token;
    } else {
      return {
        type: "nft",
        address: address,
        standard,
        symbol: symbol || name.toUpperCase(),
        name,
      } satisfies Token;
    }
  }


  /**
   * Return the balance of multiple addresses in wei.
   * @param walletAddresses
   * @param blockNumber
   * @returns
   */
  async getBalances(walletAddresses: Address[], blockNumber?: BlockNumber | null) {
    const map: Record<Address, bigint> = {};

    /**
     * Given that we don't pay for the proxies Monad
     * RPC, we can use it for bulk balance fetching.
     * However for other chains, we are limited by
     * QuickNode request quotas, we use the default
     * RPC for that.
     * @note This is a workaround for now, in the future
     * it will also cause issues for chains other than Monad.
     */
    const chunks = _.chunk(walletAddresses, 500);
    await Promise.all(chunks.map(async chunk => {
      await this.publicClient.multicall({
        blockNumber: blockNumber ?? undefined,
        contracts: chunk.map(address => ({
          address: this.viem.contracts!.multicall3!.address,
          abi: [{
            inputs: [{ name: "addr", type: "address" }],
            name: "getEthBalance",
            outputs: [{ name: "balance", type: "uint256" }],
            stateMutability: "view",
            type: "function",
          }] as const,
          functionName: "getEthBalance",
          args: [address],
        })),
        allowFailure: false,
        batchSize: 1024 * 8 // To avoid over batching and hitting RPC rate limits
      }).then(values => {
        for (let i = 0; i < values.length; i++) {
          map[chunk[i]] = values[i];
        }
      });
    }));


    return map;
  }


  /**
   * Return the balance of a given wallet address in wei.
   * @note This is a wrapper around @see getBalances.
   * @param walletAddress
   * @param blockNumber
   * @returns
   */
  async getBalance(walletAddress: Address, blockNumber?: BlockNumber | null) {
    const map = await this.getBalances([walletAddress], blockNumber);
    return map[walletAddress];
  }


  /**
   * Classify accounts via Lens.classifyAccounts as EOA, contract, or
   * EIP-7702 delegated EOA. Batched per 200 to avoid OOG on low-gas
   * chains. The result is aligned with `accounts`.
   */
  async classifyAccounts(
    accounts: Address[],
    blockNumber?: BlockNumber | null
  ): Promise<AccountType[]> {
    if (accounts.length === 0) return [];

    const lensAddress = this.chain.contracts.Lens;
    if (!lensAddress) throw new ContractNotConfiguredError("Lens", this.chainTag);

    const chunks = await Promise.all(_.chunk(accounts, 200).map(async (chunk) => {
      const codes = await this.publicClient.readContract({
        address: lensAddress,
        abi: lensAbi,
        functionName: "classifyAccounts",
        args: [chunk],
        blockNumber: blockNumber ?? undefined,
      });
      return codes.map((code) => ACCOUNT_TYPE_BY_CODE[code] ?? "eoa");
    }));

    return chunks.flat();
  }

  async getErc721Balances(tokenAddress: Address, walletAddresses: Address[]) {

    if (walletAddresses.length === 0) return {};

    const walletSet = new Set(walletAddresses);
    const map = walletAddresses.reduce((acc, address) => {
      acc[address] = [];
      return acc;
    }, {} as Record<string, bigint[]>);


    /**
     * Check if the collection is enumerable. If it is, we use
     * the Lens contract to get the balances. Otherwise, we scan
     * the entire token range via multicall.
     */
    const [
      { result: isEnumerable },
      { result: totalSupply }
    ] = await this.publicClient.multicall({
      allowFailure: true,
      contracts: [
        {
          address: tokenAddress,
          abi: [{
            inputs: [{ name: "interfaceId", type: "bytes4" }],
            name: "supportsInterface",
            outputs: [{ name: "", type: "bool" }],
            stateMutability: "view",
            type: "function",
          }] as const,
          functionName: "supportsInterface",
          args: ["0x780e9d63"],
        },
        {
          address: tokenAddress,
          abi: erc721Abi,
          functionName: "totalSupply",
        },
      ],
    });

    if (typeof totalSupply === "bigint" && totalSupply <= 0n) return map;


    const lensAddress = this.chain.contracts.Lens;
    if (isEnumerable && lensAddress) {
      const chunks = _.chunk(walletAddresses, 100);
      const balances = await this.publicClient.multicall({
        allowFailure: false,
        batchSize: 1024 * 8,
        contracts: chunks.map(chunk => ({
          address: lensAddress,
          abi: lensAbi,
          functionName: "getErc721Balances",
          args: [tokenAddress, chunk],
        } as const)),
      });

      for (let c = 0; c < chunks.length; c++) {
        const chunk = chunks[c]!;
        const rows = balances[c]!;
        for (let i = 0; i < chunk.length; i++) {
          map[chunk[i]!] = [...rows[i]!];
        }
      }
      return map;
    }


    if (typeof totalSupply !== "bigint") throw new Error("Total supply is not available");


    // Full scan fallback
    // Cover both 0-indexed and 1-indexed sequential mints.
    const ids = Array.from({ length: Number(totalSupply) + 1 }, (_, i) => BigInt(i));
    const owners = await this.publicClient.multicall({
      allowFailure: true, // To avoid crashing when scanning non-minted tokens
      batchSize: 1024 * 8,
      contracts: ids.map(tokenId => ({
        address: tokenAddress,
        abi: erc721Abi,
        functionName: "ownerOf" as const,
        args: [tokenId] as const,
      })),
    });

    for (let i = 0; i < ids.length; i++) {
      const row = owners[i];
      if (row?.status !== "success") continue;
      const wallet = normalizeAddress(row.result);
      if (!walletSet.has(wallet)) continue;
      map[wallet]!.push(ids[i]!);
    }
    return map;
  }


  /**
   * Fetch ERC1155 balances for multiple wallet addresses.
   * @param token
   * @param walletAddresses
   * @returns
   */
  async getErc1155Balances(tokenAddress: Address, walletAddresses: Address[]) {

    const chunks   = _.chunk(walletAddresses, 50);
    const balances = walletAddresses.reduce((acc, address) => {
      acc[address] = [];
      return acc;
    }, {} as Record<string, bigint[]>);


    await this.publicClient.multicall({
      contracts: chunks.map(chunk => ({
        address: tokenAddress,
        abi: ercAbi.erc1155,
        functionName: "balanceOfBatch",
        args: [chunk, chunk.map(() => 0n)] as const,
      })),
      allowFailure: false,
      batchSize: 1024 * 8 // To avoid over batching and hitting RPC rate limits
    }).then(values => {
      const results = values.flat();
      for (let i = 0; i < results.length; i++) {
        const walletAddress = walletAddresses[i];
        balances[walletAddress] = Array.from({
          length: Number(results[i])
        }).fill(0n) as bigint[];
      }
    });

    return balances;
  }


  /**
   * Fetch ERC20 balances for multiple wallet addresses.
   * @param tokenAddress
   * @param walletAddresses
   * @returns
   */
  async getErc20Balances(tokenAddress: Address, walletAddresses: Address[]) {
    const map: Record<string, bigint> = {};
    await this.publicClient.multicall({
      contracts: walletAddresses.map(address => ({
        address: tokenAddress,
        abi: erc20Abi,
        functionName: "balanceOf",
        args: [address],
      })),
      allowFailure: false,
      batchSize: 1024 * 8 // To avoid over batching and hitting RPC rate limits
    }).then(values => {
      for (let i = 0; i < values.length; i++) {
        map[walletAddresses[i]] = values[i] as bigint;
      }
    });

    return map;
  }


  /**
   * Return the balance of a given ERC20 token for a given wallet address.
   * @note This is a wrapper around @see getErc20Balances.
   * @param tokenAddress
   * @param walletAddress
   * @returns
   */
  async getErc20Balance(tokenAddress: Address, walletAddress: Address) {
    const balance = await this.getErc20Balances(tokenAddress, [walletAddress]);
    return balance[walletAddress];
  }


  /**
   * Return the latest base fee for the chain.
   * @returns
   */
  async getBaseFee() {
    try {
      const block = await this.getLastestBlock();
      if (!block) return 0n;
      return block.baseFeePerGas!;
    } catch (error: any) {
      console.error(error);
      return 0n;
    }
  }


  /**
   * Return a different polling interval for a given chain.
   * @static
   * @param chainTag
   * @returns
   */
  static getBlockPollingInterval(chainTag: ChainTag) {
    switch (chainTag) {
      case "MONAD": return 400;
      case "BSC": return 150;
      case "HYPERLIQUID": return 500;
      case "SHAPE": return 1000;
      case "SOMNIA": return 200;
      default: return 1000;
    }
  }


  /**
   * Return a chain-aware polling interval for waitForTransactionReceipt.
   * Slower chains (longer block times) use longer intervals to avoid
   * wasting RPC calls polling for blocks that haven't been produced yet.
   * @static
   * @param chainTag
   * @returns interval in milliseconds
   */
  static getReceiptPollingInterval(chainTag: ChainTag) {
    switch (chainTag) {
      case "ETHEREUM": return 4_000;
      case "BASE":
      case "MEGAETH":
      case "MONAD":
      case "BSC": return 1_000;
      default: return 2_000;
    }
  }


  /**
   * Wait for a transaction receipt by polling the RPC. Concurrent calls
   * for different hashes are automatically batched by the DataLoader into
   * a single HTTP request.
   *
   * Polling uses exponential backoff starting at the chain's receipt polling
   * interval, doubling after each miss up to 30s.
   * @param hash - The transaction hash to wait for
   * @param timeout - Maximum time to wait in milliseconds (default: 90s)
   * @returns The transaction receipt
   * @throws WaitForTransactionReceiptTimeoutError if timeout is reached
   * @throws Error if RPC returns an error or response is malformed
   */
  async pollTransactionReceipt(
    hash: Hash,
    timeout: number = 90_000,
  ): Promise<TransactionReceipt> {

    const interval = EvmManager.getReceiptPollingInterval(this.chainTag);
    const maxInterval = 30_000;
    const deadline = Date.now() + timeout;
    let delay = interval;
    let lastError: Error | undefined;
    let sawNull = false;

    while (Date.now() < deadline) {
      const [result, err] = await catchBlock(() => this.receiptLoader.load(hash));
      if (err) {
        lastError = err;
      } else if (result) {
        return result;
      } else {
        sawNull = true;
      }

      const remaining = deadline - Date.now();
      if (remaining <= 0) break;

      await new Promise(r => setTimeout(r, Math.min(delay, remaining)));
      delay = Math.min(delay * 2, maxInterval);
    }

    if (lastError && !sawNull) throw lastError;
    throw new WaitForTransactionReceiptTimeoutError({ hash });
  }


  /**
   * Return the transaction receipt for a given hash.
   * Pending / unknown is `null`. RPC and parse failures throw.
   * @param hash
   * @returns
   */
  async getTransactionReceipt(hash: Hash): Promise<TransactionReceipt | null> {
    return this.receiptLoader.load(hash);
  }


  /**
   * The block polling interval for the current chain.
   * @note this is a getter different from the static method
   * @see getBlockPollingInterval.
   * @returns
   */
  get blockPollingInterval() {
    return EvmManager.getBlockPollingInterval(this.chainTag);
  }


  /**
   * Get the latest block on the chain.
   * @param param0
   * @returns
   */
  async getLastestBlock(
    {
      includeTransactions = false,
      maxRetries = 3
    }:
    {
      includeTransactions?: boolean,
      maxRetries?: number
    } = {}) {

    return retry(async () => {
      const [block, fees] = await Promise.all([
        this.publicClient.getBlock({ includeTransactions }),
        this.chainTag === "POLYGON"
          ? this.publicClient.estimateFeesPerGas()
          : null,
      ]);
      if (fees) block.baseFeePerGas = fees.maxFeePerGas ?? fees.gasPrice;
      return block;
    }, {
      maxRetries,
      exponential: false,
    });
  }


  /**
   * Send a lightweight RPC call (eth_chainId) to establish the
   * underlying HTTP/TLS connection. Subsequent requests to the
   * same RPC endpoint reuse the warm connection, avoiding TLS
   * handshake latency on the critical path.
   */
  async warmup() {
    await this.publicClient.getChainId();
  }



  /**
   * Whether the chain's RPC supports debug_traceCall.
   *
   * Memoised in-process on this manager instance:
   * - `true` and `false` are stored for the life of the process. `??=` cannot
   *   pin a `false`, so the settled boolean is checked with `!== undefined`.
   * - Concurrent callers share one in-flight probe instead of each firing
   *   their own.
   * - A throw is not cached as `false` and does not stick: the in-flight
   *   promise is dropped so the next call retries. A later success or
   *   "method not found" then pins the boolean.
   *
   * In-process only. @umi/shared has no Redis client and must not depend
   * on @umi/core just to share this flag across processes.
   * @returns
   */
  async supportsDebugTrace() {
    if (this.debugTraceSupported !== undefined) return this.debugTraceSupported;

    if (!this.debugTraceProbe) {
      this.debugTraceProbe = probeDebugTraceSupport(this.debugClient)
        .then((supported) => {
          this.debugTraceSupported = supported;
          return supported;
        })
        .finally(() => {
          this.debugTraceProbe = undefined;
        });
    }

    return this.debugTraceProbe;
  }


  getWalletClient(pk: Hex, accountOpts?: PrivateKeyToAccountOptions) {
    const safePk = pk.startsWith("0x") ? pk : `0x${pk}`;
    const account = privateKeyToAccount(
      safePk as Hex,
      accountOpts
    );
    return extendClient(createWalletClient({
      account,
      chain: this.viem,
      transport: this.httpTransport
    }) as any, this.chainTag) as unknown as WalletClient<Transport, typeof this.viem, typeof account>;
  }


  /**
   * Return a type contract instance for a given address and ABI.
   * @param address
   * @param abi
   * @returns
   */
  getContract<
    TAbi extends readonly unknown[],
    TWallet extends WalletClient = WalletClient,
  >(
    address: Address,
    abi: TAbi,
    walletClient?: TWallet,
  ): GetContractReturnType<TAbi, { public: PublicClient; wallet: TWallet }> {
    return getContract({
      address,
      abi,
      client: (walletClient
        ? { public: this.publicClient, wallet: walletClient }
        : this.publicClient) as any,
    }) as GetContractReturnType<TAbi, { public: PublicClient; wallet: TWallet }>;
  }


  /**
   * Sign a message with a private key.
   * @param pk
   * @param message
   * @returns
   */
  async signMessage(pk: Hex, message: string) {
    const client = this.getWalletClient(pk);
    return client.signMessage({
      account: client.account,
      message
    });
  }


  /**
   * Return an account that we know have a lot of funds so we
   * can perform fee estimations without caring for participants
   * balance.
   * @returns
   */
  getFundedAccount() {
    // Choose an account that have a balance
    if (this.chainTag === "SHAPE")    return "0x4200000000000000000000000000000000000016";
    if (this.chainTag === "ABSTRACT") return "0xe8a493f2697ca66568be98a4b5b389fba37a8a49";
    if (this.chainTag === "SOPHON")   return "0x823f34b8b36d00d06A231C436052D2c0ce8D548f";
    // Arc reverts native value to/from address(0) with "Zero address not allowed".
    if (this.chainTag === "ARC")      return "0x000000000000000000000000000000000000dEaD";
    return zeroAddress;
  }


  /**
   * Estimate the gas data (fee and gas limit) for a transaction.
   * @param account
   * @param to
   * @param value
   * @param data
   * @param gas
   * @returns
   */
  async estimateFee({
    account,
    to,
    value = 0n,
    data = "0x",
    gas,
    client,
    gasBufferBps = 1000, // 10%
    stateOverride = [],
    authorizationList,
  }: {
    account: Hex,
    to: Hex,
    value?: bigint,
    data?: Hex,
    gas?: bigint,
    client?: ExtendedClient,
    gasBufferBps?: number,
    stateOverride?: StateOverride,
    authorizationList?: SignedAuthorization[],
  }): Promise<GasFeeEstimation> {

    client = client ?? this.publicClient;
    const defaultGas = this.chainTag === "MEGAETH" ? 60000n : 23000n; // Default gas limit for simple transfers
    const eip7702 = authorizationList?.length
      ? { authorizationList }
      : {};

    if (this.chain.tag === "ARBITRUM") {

      const provider = viemTransportToProvider(this.publicClient);
      const nodeInterface = NodeInterface__factory.connect(NODE_INTERFACE_ADDRESS, provider);

      const { baseFee, gasEstimate } = await nodeInterface
        .callStatic
        .gasEstimateComponents(
          to,
          false,
          data as BytesLike,
          { blockTag: "latest", value, from: account }
        );

      gas = gas ?? bufferBigint(gasEstimate.toBigInt(), gasBufferBps);
      const feePerGas = baseFee.toBigInt();
      const fee = gas * feePerGas;
      return { fee, gas, feePerGas };
    }

    if (this.chain.opStack) {

      const [l1Fee, l2Gas, l2GasPrice] = await Promise.all([
        client.estimateL1Fee({ to, data, account, chain: this.viem }),
        gas !== undefined
          ? 0n
          : data !== "0x" ? client
            .estimateGas({ to, data, value, account, stateOverride, ...eip7702 }) : defaultGas,
        client.getGasPrice(),
      ]);

      gas = gas ?? bufferBigint(l2Gas, gasBufferBps);
      const feePerGas = l2GasPrice;
      const fee = l1Fee + gas * feePerGas;
      return { fee, gas, feePerGas };
    }

    if (this.chain.zkSync) {

      const l2Gas = gas !== undefined
        ? 0n
        : data !== "0x"
          ? await client.estimateGas({ account, to, data, value, stateOverride, ...eip7702 })
          : defaultGas;

      const { maxFeePerGas } = await client
        .estimateFee({
          to,
          data,
          gas,
          value,
          account,
          chain: this.viem,
        });

      gas = gas ?? bufferBigint(l2Gas, gasBufferBps);
      const feePerGas = maxFeePerGas;
      const fee = gas * feePerGas;
      return { gas, fee, feePerGas };
    }

    const [contractGas, { maxFeePerGas }] = await Promise.all([
      // If no gas is provided, estimate the gas based on the data
      // If the data is empty, use the default gas limit (for simple transfers)
      gas !== undefined
        ? 0n
        : data !== "0x"
          ? client.estimateGas({ to, data, account, value, stateOverride, ...eip7702 })
          : defaultGas,
      client.estimateFeesPerGas(),
    ]);
    gas = gas ?? bufferBigint(contractGas, gasBufferBps);

    const feePerGas = maxFeePerGas;
    const fee = gas * feePerGas;
    return { gas, fee, feePerGas };
  }


  /**
   * Return the approval
   * @param token
   * @param wallets
   * @param erc20Balances
   * @returns
   */
  async getErcApprovalStatus(
    token: Token,
    wallets: Address[],
    erc20Balances: Record<Address, bigint> = {}
  ) {

    const disperseAddress = this.chain.contracts.Disperse;
    if (!disperseAddress) throw new ContractNotConfiguredError("Disperse", this.chainTag);

    return await this.publicClient.multicall({
      allowFailure: false,
      contracts: wallets.map((wallet) => {
        if (token.standard === "erc20") {
          return {
            address: token.address,
            abi: erc20Abi,
            functionName: "allowance",
            args: [wallet, disperseAddress],
          } as const;
        }

        return {
          address: token.address,
          abi: erc721Abi, // Same for ERC721 and ERC1155
          functionName: "isApprovedForAll",
          args: [wallet, disperseAddress],
        } as const;
      })
    }).then(results => {
      return wallets.reduce((acc, wallet, i) => {
        const result = results[i];
        if (_.isBoolean(result)) acc[wallet] = !!result; // ERC721/1155 returns boolean
        if (typeof result === "bigint") { // ERC20 returns bigint
          const balance = erc20Balances[wallet] || 0n;
          acc[wallet] = result >= balance;
        }
        return acc;
      }, {} as Record<Address, boolean>);
    });

  }


  /**
   * Approve the disperse contract to transfer ERC tokens
   * (ERC20, ERC721, ERC1155). Will not approve if the token
   * is already approved.
   * @param token
   * @param wallet
   * @param walletPk
   * @param skipApprovalCheck
   * @returns
   */
  async approveErcTransfers(
    wallet: HydratedWallet,
    token: Token,
    pk: Hex,
    skipApprovalCheck = false,
  ) {

    const disperseAddress = this.chain.contracts.Disperse;
    if (!disperseAddress) throw new ContractNotConfiguredError("Disperse", this.chainTag);

    const walletAddress = wallet.address;
    const client        = this.getWalletClient(pk);
    const isErc20       = token.standard === "erc20";
    const isErc721      = token.standard === "erc721";
    const isErc1155     = token.standard === "erc1155";
    let txHash: `0x${string}`;

    if (!isErc20 && !isErc721 && !isErc1155) throw new UnknownTokenStandardError(token.address);


    if (isErc20) {
      const contract  = this.getContract(token.address, erc20Abi, client);
      const balance   = wallet.balance(this.chainTag, token.address).balance as bigint;
      if (!skipApprovalCheck) {
        const allowance = await contract.read.allowance([walletAddress, disperseAddress]);
        if (allowance >= balance) return;
      }
      txHash = await contract.write.approve([disperseAddress, balance]);
    }

    else if (isErc721 || isErc1155) {
      const contract = this.getContract(token.address, erc721Abi, client);
      if (!skipApprovalCheck) {
        const approved = await contract.read.isApprovedForAll([walletAddress, disperseAddress]);
        if (approved) return;
      }
      txHash = await contract.write.setApprovalForAll([disperseAddress, true]);
    }

    await this.publicClient
      .waitForTransactionReceipt({ hash: txHash! })
      .then(receipt => {
        if (receipt.status === "reverted") throw receipt;
      });
  }


  /**
   * Estimate the gas fees for transferring tokens to multiple recipients.
   * @param token
   * @param recipients
   * @param senders
   * @param tokenBalances
   * @returns
   */
  async estimateTransfers({
    token,
    recipients,
    senders = [],
    tokenBalances = {},
  }: {
    token: Token,
    recipients: Address[],
    senders?: Address[],
    tokenBalances?: Record<Address, bigint | bigint[]>,
  }) {

    const disperseAddress = this.chain.contracts.Disperse;
    if (!disperseAddress) throw new ContractNotConfiguredError("Disperse", this.chainTag);

    const estimates = new Map<number, GasFeeEstimation>();
    const queue = new Queue({ intervalCap: 50, interval: 1000 });
    const placeholderAccount = this.getFundedAccount();
    const errors: any[] = [];

    if (token.standard === "native") { // Native transfers
      for (let i = 1; i <= recipients.length; i++) {
        queue.add(async () => {
          const [,err] = await catchBlock(async () => {

            const to    = placeholderAccount;
            const value = BigInt(_.sum(_.range(i).map(() => 1n)));
            const data  = i === 1 ? "0x" : encodeFunctionData({
              abi: disperseAbi,
              functionName: "disperseNative",
              args: [recipients.slice(0, i), _.range(i).map(() => 1n)],
            });
            await this.estimateFee({
              account: placeholderAccount,
              to,
              value,
              data
            }).then(estimate => { estimates.set(i, estimate); });

          });

          if (err) errors.push(err);
        });
      }
      await queue.onIdle();

      if (errors.length > 0) {
        console.error(errors.length > 1 ? errors : errors[0]);
        throw new AggregateError(errors, "Failed to estimate gas fees");
      }
      return { estimates, disperseMode: "contract" as const };
    }

    if (token.standard === "erc20") { // ERC20 transfers

      // Fire approval, single-transfer, and disperse probe estimates in parallel.
      // All three are independent and land in the same transport batch window.
      let disperseMode: "contract" | "direct" = "contract";

      const [approvalEstimate, singleEstimate, probeResult] = await Promise.all([
        this.estimateFee({ // approvalEstimate
          account: senders[0],
          to: token.address,
          value: 0n,
          data: encodeFunctionData({
            abi: erc20Abi,
            functionName: "approve",
            args: [disperseAddress, 0n],
          })
        }),
        this.estimateFee({ // singleEstimate
          account: senders[0],
          to: token.address,
          value: 0n,
          data: encodeFunctionData({
            abi: erc20Abi,
            functionName: "transfer",
            args: [recipients[0] ?? zeroAddress, 0n],
          }),
          gasBufferBps: 2000, // 20% buffer — zero-amount estimates undercount storage ops
        }),
        recipients.length > 1
          ? catchBlock(async () => { // probeResult
            return this.estimateFee({
              account: senders[0],
              to: disperseAddress,
              value: 0n,
              data: encodeFunctionData({
                abi: disperseAbi,
                functionName: "disperseERC20",
                args: [
                  token.address,
                  recipients.slice(0, 2),
                  [0n, 0n]
                ],
              }),
              gasBufferBps: 2000,
            });
          })
          : [] as const,
      ]);

      estimates.set(1, singleEstimate);

      /**
       * If we have more than one recipient, we check if the probe result
       * is succcessful. If it's not it means we can't use the disperse contract
       * so we use direct mode instead.
       */
      if (recipients.length > 1) {
        const [probeValue, probeErr] = probeResult;
        if (probeErr) disperseMode = "direct";
        else if (probeValue) {
          // probeValue is the gas fee estimation for the disperse contract
          // that we ran for 2 recipients so we can already use it.
          estimates.set(2, probeValue);
        }
      }

      if (disperseMode === "direct") {
        // i=1 already set above; direct mode scales linearly (one tx per recipient)
        for (let i = 2; i <= recipients.length; i++) {
          estimates.set(i, {
            fee: singleEstimate.fee * BigInt(i),
            gas: singleEstimate.gas * BigInt(i),
            feePerGas: singleEstimate.feePerGas,
          });
        }
      } else {
        // i=1 set above, i=2 set from the disperse probe; estimate the rest on-chain
        const errors: Error[] = [];
        for (let i = 3; i <= recipients.length; i++) {
          queue.add(async () => {
            const recipientSubset = recipients.slice(0, i);
            const data = encodeFunctionData({
              abi: disperseAbi,
              functionName: "disperseERC20",
              args: [
                token.address,
                recipientSubset, recipientSubset.map(() => 0n)
              ],
            });
            const [, err] = await catchBlock(async () => {
              await this.estimateFee({
                account: senders[0],
                to: disperseAddress,
                value: 0n,
                data,
                gasBufferBps: 2000,
              }).then(estimate => { estimates.set(i, estimate); });
            });
            if (err) errors.push(err instanceof Error ? err : new Error(String(err)));
          });
        }
        await queue.onIdle();
        if (errors.length > 0) {
          throw new AggregateError(errors, "Failed to estimate ERC20 gas fees");
        }
      }

      return { approvalEstimate, estimates, disperseMode };
    }

    if (token.standard === "erc721" || token.standard === "erc1155") { // ERC721 and ERC1155 transfers
      const nftBalances = tokenBalances as Record<Address, bigint[]>;
      let disperseMode: "contract" | "direct" = "contract";

      // Finds the first sender with tokens to estimate the single transfer.
      const firstSenderWithTokens = senders.find(s => (nftBalances[s] || []).length > 0);

      if (firstSenderWithTokens) {

        const senderTokens   = nftBalances[firstSenderWithTokens];
        const probeRecipient = recipients[0] ?? zeroAddress;

        // Encode the single transfer call.
        const singleEstimateCall = token.standard === "erc721"
          ? encodeFunctionData({
            abi: erc721Abi,
            functionName: "safeTransferFrom",
            args: [
              firstSenderWithTokens,
              probeRecipient,
              senderTokens[0]
            ]
          })
          : encodeFunctionData({
            abi: ercAbi.erc1155,
            functionName: "safeTransferFrom",
            args: [
              firstSenderWithTokens,
              probeRecipient,
              senderTokens[0],
              1n,
              "0x" as const
            ]
          });


        // Encode the disperse call.
        const probeCall = token.standard === "erc721"
          ? encodeFunctionData({
            abi: disperseAbi,
            functionName: "disperseERC721",
            args: [
              token.address,
              [probeRecipient],
              [[senderTokens[0]]]
            ]
          })
          : encodeFunctionData({
            abi: disperseAbi,
            functionName: "disperseERC1155",
            args: [
              token.address,
              [probeRecipient],
              [[senderTokens[0]]],
              [[1n]],
              "0x" as const
            ]
          });

        // Estimate the single transfer and the disperse probe in parallel.
        const [singleEstimate, [, probeErr]] = await Promise.all([
          this.estimateFee({
            account: firstSenderWithTokens,
            to: token.address,
            value: 0n,
            data: singleEstimateCall
          }),
          catchBlock(async () => {
            await this.estimateFee({
              account: firstSenderWithTokens,
              to: disperseAddress,
              value: 0n,
              data: probeCall
            });
          }),
        ]);

        estimates.set(1, singleEstimate);

        // If we have a probe error, it means we can't use the disperse contract
        // so we use direct mode instead.
        if (probeErr) disperseMode = "direct";
      }

      // If we are using the disperse contract then we estimate approval of the NFT to the disperse contract.
      const approvalEstimate = disperseMode === "contract" && recipients.length > 1 ? await this.estimateFee({
        account: placeholderAccount,
        to: token.address,
        value: 0n,
        data: encodeFunctionData({
          abi: erc721Abi,
          functionName: "setApprovalForAll",
          args: [disperseAddress, true],
        })
      }) : { fee: 0n, feePerGas: 0n, gas: 0n };

      if (disperseMode === "direct") {
        // Direct mode: each NFT is an individual safeTransferFrom
        const singleEstimate = estimates.get(1);
        if (singleEstimate) {
          for (const sender of senders) {
            const senderTokens = nftBalances[sender] || [];
            if (senderTokens.length === 0) continue;
            for (let i = 1; i <= recipients.length; i++) {
              for (let j = 1; j <= senderTokens.length; j++) {
                const feeKey = i * 10000 + j;
                estimates.set(feeKey, {
                  fee: singleEstimate.fee * BigInt(j),
                  gas: singleEstimate.gas * BigInt(j),
                  feePerGas: singleEstimate.feePerGas,
                });
              }
            }
          }
        }
      } else {
        // Contract mode: estimate disperse calls
        const errors: Error[] = [];
        if (token.standard === "erc721") {
          for (const sender of senders) {
            const senderTokens = nftBalances[sender] || [];
            if (senderTokens.length === 0) continue;
            for (let i = 1; i <= recipients.length; i++) {
              const recipientSubset = recipients.slice(0, i);
              for (let j = 1; j <= senderTokens.length; j++) {
                queue.add(async () => {
                  const feeKey = i * 10000 + j;

                  if (i === 1 && j === 1) {
                    const data = encodeFunctionData({
                      abi: erc721Abi,
                      functionName: "safeTransferFrom",
                      args: [sender, recipientSubset[0], senderTokens[0]]
                    });
                    await this.estimateFee({
                      account: sender,
                      to: token.address,
                      value: 0n,
                      data
                    }).then(estimate => {
                      estimates.set(feeKey, estimate);
                      estimates.set(1, estimate);
                    });
                    return;
                  }

                  const tokensToDistribute = senderTokens.slice(0, j);
                  const tokenIdsBatch: bigint[][] = Array(i).fill(0).map(() => []);
                  for (let tokenIndex = 0; tokenIndex < j; tokenIndex++) {
                    const recipientIndex = tokenIndex % i;
                    tokenIdsBatch[recipientIndex].push(tokensToDistribute[tokenIndex]);
                  }

                  if (tokenIdsBatch.some(batch => batch.length === 0)) return;

                  const [, err] = await catchBlock(async () => {
                    await this.estimateFee({
                      account: sender,
                      to: disperseAddress,
                      value: 0n,
                      data: encodeFunctionData({
                        abi: disperseAbi,
                        functionName: "disperseERC721",
                        args: [token.address, recipientSubset, tokenIdsBatch]
                      })
                    }).then(estimate => { estimates.set(feeKey, estimate); });
                  });
                  if (err) errors.push(err instanceof Error ? err : new Error(String(err)));
                });
              }
            }
          }
        }

        if (token.standard === "erc1155") {
          for (const sender of senders) {
            const senderTokens = nftBalances[sender] || [];
            if (senderTokens.length === 0) continue;
            const tokenGroups = _.groupBy(senderTokens, token => token.toString());
            const uniqueTokenIds = Object.keys(tokenGroups).map(id => BigInt(id));
            for (let i = 1; i <= recipients.length; i++) {
              const recipientSubset = recipients.slice(0, i);
              for (let j = 1; j <= Math.min(uniqueTokenIds.length, 10); j++) {
                queue.add(async () => {
                  const feeKey = i * 10000 + j;

                  if (i === 1 && j === 1) {
                    const data = encodeFunctionData({
                      abi: ercAbi.erc1155,
                      functionName: "safeTransferFrom",
                      args: [sender, recipientSubset[0], senderTokens[0], 1n, "0x"]
                    });
                    await this.estimateFee({
                      account: sender,
                      to: token.address,
                      value: 0n,
                      data
                    }).then(estimate => {
                      estimates.set(feeKey, estimate);
                      estimates.set(1, estimate);
                    });
                    return;
                  }

                  const tokenIdsPerRecipient: bigint[][] = [];
                  const amountsPerRecipient: bigint[][] = [];
                  for (let r = 0; r < i; r++) {
                    const selectedIds = uniqueTokenIds.slice(0, j);
                    const amounts = selectedIds.map(id => BigInt(tokenGroups[id.toString()].length) || 1n);
                    tokenIdsPerRecipient.push(selectedIds);
                    amountsPerRecipient.push(amounts);
                  }

                  if (
                    tokenIdsPerRecipient.some(ids => ids.length === 0) ||
                    amountsPerRecipient.some(amounts => amounts.every(a => a === 0n))
                  ) return;

                  const [, err] = await catchBlock(async () => {
                    await this.estimateFee({
                      account: sender,
                      to: disperseAddress,
                      value: 0n,
                      data: encodeFunctionData({
                        abi: disperseAbi,
                        functionName: "disperseERC1155",
                        args: [
                          token.address,
                          recipientSubset,
                          tokenIdsPerRecipient,
                          amountsPerRecipient,
                          "0x"
                        ]
                      })
                    }).then(estimate => { estimates.set(feeKey, estimate); });
                  });
                  if (err) errors.push(err instanceof Error ? err : new Error(String(err)));
                });
              }
            }
          }
        }

        if (errors.length > 0) {
          throw new AggregateError(errors, "Failed to estimate gas fees");
        }
      }

      await queue.onIdle();
      return { approvalEstimate, estimates, disperseMode };
    }

    else throw new UnknownTokenStandardError(token.address);

  }


  /**
   * Transfer ERC tokens.
   * @param token
   * @param senderPk
   * @param transfers
   * @param explicitGas
   * @param explicitMaxFeePerGas
   * @param disperseMode
   */
  async transferErc(
    token: Token,
    senderPk: Hex,
    transfers: { to: Address, value: bigint | bigint[] }[],
    explicitGas?: bigint,
    explicitMaxFeePerGas?: bigint,
    disperseMode: "contract" | "direct" = "contract",
  ) {

    const disperseAddress = this.chain.contracts.Disperse;
    if (disperseMode === "contract" && !disperseAddress) {
      throw new ContractNotConfiguredError("Disperse", this.chainTag);
    }

    const tokenAddress = token.address;
    const client       = this.getWalletClient(senderPk);
    const recipients   = transfers.map(({ to }) => to);
    const values       = transfers.map(({ value }) => value);
    const isErc20      = token.standard === "erc20";
    const isErc721     = token.standard === "erc721";
    const isErc1155    = token.standard === "erc1155";
    let txHash: `0x${string}`;

    if (!isErc20 && !isErc721 && !isErc1155) throw new UnknownTokenStandardError(token.address);

    /**
     * Direct mode: send each transfer individually without the disperse contract.
     * 1. Build calldata for each individual tx
     * 2. Resolve gas params once (no per-tx RPC)
     * 3. Fire all sendTransaction in parallel — with all params pre-filled,
     *    viem skips prepareTransactionRequest RPCs, signs locally, and the
     *    concurrent sendRawTransaction calls are collapsed into a single
     *    HTTP request via transport-level JSON-RPC batching.
     * 4. Poll all receipts in a single batched loop.
     */
    if (disperseMode === "direct") {
      const txDataList: { to: Address; data: `0x${string}` }[] = [];

      for (const transfer of transfers) {
        const recipient = transfer.to;

        if (isErc20) {
          txDataList.push({
            to: tokenAddress,
            data: encodeFunctionData({
              abi: erc20Abi,
              functionName: "transfer",
              args: [recipient, transfer.value as bigint],
            }),
          });
        }

        if (isErc721) {
          for (const tokenId of transfer.value as bigint[]) {
            txDataList.push({
              to: tokenAddress,
              data: encodeFunctionData({
                abi: erc721Abi,
                functionName: "safeTransferFrom",
                args: [client.account.address, recipient, tokenId],
              }),
            });
          }
        }

        if (isErc1155) {
          const tokenIds = transfer.value as bigint[];
          const ids = _.uniq(tokenIds);
          const amounts = ids.map(id => BigInt(_.countBy(tokenIds.map(String))[id.toString()]));
          txDataList.push({
            to: tokenAddress,
            data: encodeFunctionData({
              abi: ercAbi.erc1155,
              functionName: "safeBatchTransferFrom",
              args: [client.account.address, recipient, ids, amounts, "0x"],
            }),
          });
        }
      }

      // Compute fees, gas and nonce in bulk so when we run
      // multiple sendTransaction calls, we don't make repetitive gas + fees + nonce RPC calls.
      const [{ maxFeePerGas, maxPriorityFeePerGas }, gas, nonce] = await Promise.all([
        explicitMaxFeePerGas
          ? {
            maxFeePerGas: explicitMaxFeePerGas,
            // Thanks to EIP-1559, we can set the priority fee to the max fee per gas
            // and it will be clamped by the protocol without causing an error.
            maxPriorityFeePerGas: explicitMaxFeePerGas
          }
          : this.publicClient.estimateFeesPerGas(),

        explicitGas
          ? explicitGas / BigInt(txDataList.length)
          : this.publicClient.estimateGas({
            account: client.account.address,
            ...txDataList[0],
          }).then(g => bufferBigint(g, 1000)),

        this.publicClient.getTransactionCount({ address: client.account.address }),
      ]);


      const hashes = await Promise.all(
        txDataList.map((tx, i) =>
          client.sendTransaction({
            ...tx,
            nonce: nonce + i,
            gas,
            maxFeePerGas,
            maxPriorityFeePerGas,
          })
        )
      );

      const receipts = await Promise.all(hashes.map(hash => this.pollTransactionReceipt(hash)));
      const reverted = receipts.find(r => r.status === "reverted");
      if (reverted) throw reverted;
      return hashes;
    }

    // Contract mode (or single transfer which doesn't use disperse)
    if (isErc20) {

      const to = transfers.length === 1 ? token.address : disperseAddress;
      const data = (() => {
        if (transfers.length === 1) {
          return encodeFunctionData({
            abi: erc20Abi,
            functionName: "transfer",
            args: [recipients[0], values[0] as bigint]
          });
        }
        return encodeFunctionData({
          abi: disperseAbi,
          functionName: "disperseERC20",
          args: [tokenAddress, recipients, values as bigint[]]
        });
      })();

      const gas = await this.publicClient
        .estimateGas({ account: client.account.address, to, data, value: 0n })
        .then(g => bufferBigint(g, 1000));

      txHash = await client.sendTransaction({
        to,
        data,
        gas,
        maxFeePerGas: explicitMaxFeePerGas,
        maxPriorityFeePerGas: explicitMaxFeePerGas,
      });
    }

    if (isErc721) {

      const data = (() => {
        if (transfers.length === 1 && (transfers[0].value as bigint[]).length === 1) {
          const values = transfers[0].value as bigint[];
          const [recipient] = recipients;
          return encodeFunctionData({
            abi: erc721Abi,
            functionName: "safeTransferFrom",
            args: [
              client.account.address,
              recipient,
              values[0],
            ]
          });
        }
        return encodeFunctionData({
          abi: disperseAbi,
          functionName: "disperseERC721",
          args: [
            tokenAddress,
            recipients,
            values.map(i => (i as bigint[]).map(j => BigInt(j))),
          ]
        });
      })();


      const tokenIds = (values as bigint[]).flat();
      txHash = await client.sendTransaction({
        to: tokenIds.length === 1 ? token.address : disperseAddress,
        data,
        gas: explicitGas,
        maxFeePerGas: explicitMaxFeePerGas,
        maxPriorityFeePerGas: explicitMaxFeePerGas,
      });
    }

    if (isErc1155) {

      const data = (() => {
        if (transfers.length === 1) {
          const values = transfers[0].value as bigint[];
          const ids = _.uniq(values);
          const amounts = ids.map(id => BigInt(_.countBy(values)[id.toString()]));
          const [recipient] = recipients;
          return encodeFunctionData({
            abi: ercAbi.erc1155,
            functionName: "safeBatchTransferFrom",
            args: [
              client.account.address,
              recipient,
              ids,
              amounts,
              "0x"
            ]
          });
        }

        const items = transfers.map(transfer => {
          const grouped = _.groupBy(transfer.value as bigint[]);
          const ids     = _.keys(grouped).map(id => BigInt(id));
          const amounts = ids.map(id => BigInt(grouped[id.toString()].length));
          return { ids, amounts };
        });
        const ids     = items.map(item => item.ids);
        const amounts = items.map(item => item.amounts);

        return encodeFunctionData({
          abi: disperseAbi,
          functionName: "disperseERC1155",
          args: [tokenAddress, recipients, ids, amounts, "0x"],
        });
      })();


      txHash = await client.sendTransaction({
        to: transfers.length === 1 ? token.address : disperseAddress,
        data,
        maxFeePerGas: explicitMaxFeePerGas,
        maxPriorityFeePerGas: explicitMaxFeePerGas,
      });
    }

    const receipt = await this.publicClient.waitForTransactionReceipt({ hash: txHash! });
    if (receipt.status === "reverted") throw receipt;
    return [txHash!];
  }


  /**
   * Transfer native tokens.
   * @param senderPk
   * @param transfers
   * @param gas
   * @returns
   */
  async transferNative(
    pk: Hex,
    transfers: { to: Address, value: bigint }[],
    gas?: bigint,
    explicitMaxFeePerGas?: bigint,
  ) {

    const disperseAddress = this.chain.contracts.Disperse;
    if (!disperseAddress) throw new Error("Disperse contract not configured for chain");

    const client      = this.getWalletClient(pk);
    const recipients  = transfers.map(({ to }) => to);
    const values      = transfers.map(({ value }) => value);
    const value       = values.reduce((acc, value) => acc + value, 0n);

    const txHash = await (async () => {

      // Don't use a contract if it's only 1 recipient transfer
      if (recipients.length === 1) {
        return client.sendTransaction({
          to: recipients[0],
          value,
          chain: this.viem,
          gas,
          maxFeePerGas: explicitMaxFeePerGas,
          maxPriorityFeePerGas: explicitMaxFeePerGas,
        });
      }

      return client.writeContract({
        address: disperseAddress,
        abi: disperseAbi,
        functionName: "disperseNative",
        args: [recipients, values],
        value,
        gas,
        maxFeePerGas: explicitMaxFeePerGas,
        maxPriorityFeePerGas: explicitMaxFeePerGas,
      });

    })();

    const receipt = await this.publicClient.waitForTransactionReceipt({
      hash: txHash,
    });
    if (receipt.status === "reverted") throw receipt;
    return txHash;
  }


  /**
   * Wait for a receipt off the promised hash that a transaction
   * sent would return.
   * @param hash
   * @param options.throwOnRevert Throw the receipt when status is not success.
   */
  async waitForReceipt(
    hash: Promise<Hash> | Hash,
    options?: { throwOnRevert?: boolean; timeout?: number },
  ) {
    const receipt = await (hash instanceof Promise
      ? hash.then(h => this.pollTransactionReceipt(h, options?.timeout))
      : this.pollTransactionReceipt(hash, options?.timeout));

    if (options?.throwOnRevert && receipt.status !== "success") {
      throw receipt;
    }
    return receipt;
  }


  /**
   * Get the nonces for a list of addresses.
   *
   * Split across concurrent JSON-RPC batch POSTs so one oversized or
   * rejected request cannot fail every address at once. The return array
   * stays positionally aligned with `addresses` because callers index by
   * input order and treat each slot as `number | Error`.
   */
  async getNonces(
    addresses: Address[],
    blockTag: BlockTag,
    opts: { throwOnError: true },
  ): Promise<number[]>;
  async getNonces(
    addresses: Address[],
    blockTag?: BlockTag,
    opts?: { throwOnError?: boolean },
  ): Promise<(number | Error)[]>;
  async getNonces(
    addresses: Address[],
    blockTag: BlockTag = "latest",
    opts: { throwOnError?: boolean } = {}
  ): Promise<(number | Error)[]> {
    const nonces = await this.nonceLoader.loadMany(addresses.map(address => ({ address, blockTag })));
    const errors = nonces.filter(nonce => nonce instanceof Error);
    if (opts.throwOnError && errors.length > 0) {
      throw new AggregateError(errors, "Failed to get nonces");
    }
    return nonces;
  }

  async getNonce(address: Address, blockTag: BlockTag = "latest") {
    return this.nonceLoader.load({ address, blockTag });
  }


  /**
   * Get the address of the multicall contract for the chain.
   * @returns
   */
  getMulticallAddress() {
    const address = this.viem.contracts!.multicall3!.address;
    if (!address) throw new Error("Multicall contract not configured for chain");
    return address;
  }


  /**
   * Make sure the private key is prefixed with "0x".
   * @param pk
   * @returns
   */
  static safePrivateKeyToAccount(pk: Hex) {
    const safePk = pk.startsWith("0x") ? pk : `0x${pk}` as Hex;
    return privateKeyToAccount(safePk);
  }


}
